import { is } from 'bpmn-js/lib/util/ModelUtil';
import { isExpanded } from 'bpmn-js/lib/util/DiUtil';
import {
  createBaustein,
  getDefinitions,
  listBausteine,
  isBaustein,
  findFlowElement,
  findPlane,
  findCallSites,
  SELECT_EVENT,
} from '../utils/bausteine';
import { importProcessAsSubProcess, insertAfterKey } from './SubProcessFromFileProvider';

/**
 * Wiederverwendbare Sub-Prozesse im Schraubenschlüssel-Menü.
 *
 * Drei Einträge, je nach markiertem Element:
 *
 *   - **Als Baustein herauslösen** (auf einem eingeklappten Sub-Prozess):
 *     verschiebt dessen Inhalt in einen eigenen Prozess und ersetzt den
 *     Sub-Prozess durch eine Call Activity darauf.
 *   - **Baustein einfügen (Verweis)**: eine Call Activity auf einen
 *     vorhandenen Baustein. Änderungen am Baustein wirken hier mit.
 *   - **Baustein einfügen (Kopie)**: der Inhalt wird kopiert und ist danach
 *     unabhängig — das bisherige Verhalten, nur ohne Umweg über eine Datei.
 *
 * Die Auswahl des Bausteins läuft über `prompt`, wie die übrigen Dialoge
 * dieses Editors auch. Ein eigener Auswahldialog wäre schöner, gehört aber
 * nicht in denselben Schritt wie die Mechanik.
 */
export default function BausteinProvider(injector) {
  this._injector = injector;
  injector.get('popupMenu').registerProvider('bpmn-replace', 510, this);
}

BausteinProvider.$inject = ['injector'];

BausteinProvider.prototype.getPopupMenuEntries = function (target) {
  return (entries) => {
    let ergebnis = entries;

    if (istHerausloesbar(target)) {
      ergebnis = insertAfterKey(
        ergebnis,
        'replace-with-subprocess-from-file',
        'baustein-extract',
        {
          label: 'Als Baustein herauslösen',
          className: 'bpmn-icon-subprocess-collapsed',
          action: () => this._herausloesen(target),
        },
      );
    }

    if (istEinfuegbar(target) && this._bausteine().length > 0) {
      ergebnis = insertAfterKey(ergebnis, 'baustein-extract', 'baustein-ref', {
        label: 'Baustein einfügen (Verweis)',
        className: 'bpmn-icon-call-activity',
        action: () => this._einfuegen(target, 'verweis'),
      });
      ergebnis = insertAfterKey(ergebnis, 'baustein-ref', 'baustein-copy', {
        label: 'Baustein einfügen (Kopie)',
        className: 'bpmn-icon-subprocess-collapsed',
        action: () => this._einfuegen(target, 'kopie'),
      });
    }

    return ergebnis;
  };
};

/**
 * Die definitions der Datei.
 *
 * Ueber getRootElements() (Mehrzahl) statt getRootElement(): Letzteres legt
 * eine Ersatzwurzel ohne DI an, wenn noch nichts importiert ist, und die
 * bringt spaeter das Speichern zu Fall.
 */
BausteinProvider.prototype._definitions = function () {
  const canvas = this._injector.get('canvas');
  const wurzeln = canvas.getRootElements?.() ?? [];
  for (const wurzel of wurzeln) {
    const defs = getDefinitions(wurzel.businessObject);
    if (defs) return defs;
  }
  return null;
};

BausteinProvider.prototype._bausteine = function () {
  const definitions = this._definitions();
  if (!definitions) return [];
  // Aufrufzahl und Umfang mitgeben: Danach entscheidet man beim Einfuegen,
  // nicht nach dem Namen allein.
  return listBausteine(definitions).map((b) => ({
    ...b,
    aufrufe: findCallSites(definitions, b.id).length,
    elemente: (b.process.flowElements || []).length,
  }));
};

// ---------------------------------------------------------------------------
// Herauslösen
// ---------------------------------------------------------------------------

/**
 * Aus einem eingeklappten Sub-Prozess einen Baustein machen.
 *
 * Die Elemente werden **verschoben**, nicht kopiert: Der Sub-Prozess
 * verschwindet und an seiner Stelle steht eine Call Activity. Wer den Inhalt
 * an beiden Stellen behalten will, fügt den Baustein danach zusätzlich ein.
 */
BausteinProvider.prototype._herausloesen = function (target) {
  return herausloesen(target, this._injector);
};

/**
 * Aus einem eingeklappten Sub-Prozess einen Baustein machen.
 *
 * Freistehend, weil zwei Wege hierher führen: der Schraubenschlüssel und das
 * Kontext-Pad. Im Menü steht der Eintrag weit unten — bei einem Sub-Prozess
 * sind dort sechzehn Einträge, von denen nur acht ohne Scrollen zu sehen sind.
 * Auffindbar ist die Aktion erst über das Kontext-Pad.
 *
 * Die Elemente werden **verschoben**, nicht kopiert: Der Sub-Prozess
 * verschwindet und an seiner Stelle steht eine Call Activity. Wer den Inhalt
 * an beiden Stellen behalten will, fügt den Baustein danach zusätzlich ein.
 */
export async function herausloesen(target, injector) {
  const moddle = injector.get('moddle');
  const bpmnFactory = injector.get('bpmnFactory');
  const bpmnReplace = injector.get('bpmnReplace');
  const modeling = injector.get('modeling');
  const canvas = injector.get('canvas');

  try {
    const wurzeln = canvas.getRootElements?.() ?? [];
    let definitions = null;
    for (const wurzel of wurzeln) {
      definitions = getDefinitions(wurzel.businessObject);
      if (definitions) break;
    }
    if (!definitions) throw new Error('Die Datei ist noch nicht bereit.');

    const quelleId = target.businessObject.id;
    const name = target.businessObject.name || 'Baustein';

    // Der Umweg über eine frisch geparste Fassung ist nötig, nicht bequem:
    // `replaceElement` löscht den Sub-Prozess samt Kindern, und diese Löschung
    // räumt dieselben Arrays leer, die der Baustein bekommen soll. Die frisch
    // geparsten Elemente hängen an keiner Zeichenfläche und bleiben unberührt.
    const { xml } = await moddle.toXML(definitions);
    const { rootElement: frisch } = await moddle.fromXML(xml, 'bpmn:Definitions');
    const quelle = findFlowElement(frisch, quelleId);
    const elemente = quelle?.flowElements || [];
    if (elemente.length === 0) throw new Error('Der Sub-Prozess enthält keine Elemente.');

    const { process, plane } = createBaustein(definitions, bpmnFactory, name);
    process.flowElements = elemente;
    for (const el of elemente) el.$parent = process;

    const quellPlane = findPlane(frisch, quelleId);
    if (quellPlane) {
      plane.planeElement = quellPlane.planeElement || [];
      for (const pe of plane.planeElement) pe.$parent = plane;
    }

    const aufruf = bpmnReplace.replaceElement(target, { type: 'bpmn:CallActivity' });
    modeling.updateProperties(aufruf, { name, calledElement: process.id });

    console.log('[Baustein] herausgelöst:', process.id, `(${elemente.length} Elemente)`);
  } catch (e) {
    console.error('[Baustein] Herauslösen fehlgeschlagen:', e);
    alert('Der Baustein konnte nicht angelegt werden: ' + e.message);
  }
}

// ---------------------------------------------------------------------------
// Einfügen
// ---------------------------------------------------------------------------

BausteinProvider.prototype._einfuegen = async function (target, art) {
  // `replaceElement` verwirft den Inhalt des ersetzten Elements. Auf einem
  // gefuellten Sub-Prozess waere das stiller Datenverlust.
  const vorhandene = target.businessObject?.flowElements?.length ?? 0;
  if (vorhandene > 0 && !globalThis.confirm(
    `„${target.businessObject.name || target.id}" enthält ${vorhandene} Element(e). `
    + 'Beim Einfügen des Bausteins geht dieser Inhalt verloren.\n\nFortfahren?'
  )) return;

  const bausteine = this._bausteine();
  const gewaehlt = await waehleBaustein(this._injector.get('eventBus'), bausteine, art);
  if (!gewaehlt) return;

  try {
    if (art === 'verweis') {
      this._alsVerweis(target, gewaehlt);
    } else {
      await this._alsKopie(target, gewaehlt);
    }
  } catch (e) {
    console.error('[Baustein] Einfügen fehlgeschlagen:', e);
    alert('Der Baustein konnte nicht eingefügt werden: ' + e.message);
  }
};

BausteinProvider.prototype._alsVerweis = function (target, baustein) {
  const bpmnReplace = this._injector.get('bpmnReplace');
  const modeling = this._injector.get('modeling');
  const aufruf = bpmnReplace.replaceElement(target, { type: 'bpmn:CallActivity' });
  modeling.updateProperties(aufruf, { name: baustein.name, calledElement: baustein.id });
};

/**
 * Als Kopie einfügen.
 *
 * Der Umweg über Serialisieren und erneutes Parsen ist Absicht: Die
 * Import-Maschine hängt die übergebenen Elemente an ihr neues Elternteil um.
 * Gäbe man ihr die Objekte des Bausteins, wäre der Baustein danach leer.
 * Frisch geparst sind es eigene Objekte, und der Weg ist derselbe wie beim
 * Import aus einer Datei — eine zweite Kopiermechanik entfällt.
 */
BausteinProvider.prototype._alsKopie = async function (target, baustein) {
  const moddle = this._injector.get('moddle');
  const { xml } = await moddle.toXML(this._definitions());
  const { rootElement: frisch } = await moddle.fromXML(xml, 'bpmn:Definitions');
  const quelle = (frisch.rootElements || []).find((e) => e.id === baustein.id);
  if (!quelle) throw new Error('Der Baustein wurde in der Datei nicht gefunden.');
  if (!(quelle.flowElements || []).length) throw new Error('Der Baustein ist leer.');
  await importProcessAsSubProcess(target, frisch, quelle, this._injector);
};

// ---------------------------------------------------------------------------
// Helfer
// ---------------------------------------------------------------------------

/**
 * Den Nutzer einen Baustein waehlen lassen.
 *
 * Bei genau einem Baustein gibt es nichts zu waehlen. Sonst wird der Dialog
 * auf der React-Seite angefordert; nimmt ihn dort niemand an, bleibt die
 * Nummerneingabe als Notnagel — besser als ein Versprechen, das nie eingeloest
 * wird.
 */
function waehleBaustein(eventBus, bausteine, art) {
  if (bausteine.length === 0) return Promise.resolve(null);
  if (bausteine.length === 1) return Promise.resolve(bausteine[0]);

  return new Promise((resolve) => {
    let erledigt = false;
    const einmal = (wahl) => { if (!erledigt) { erledigt = true; resolve(wahl); } };
    let angenommen = false;
    try {
      angenommen = eventBus.fire(SELECT_EVENT, { bausteine, art, resolve: einmal }) === true;
    } catch (e) {
      console.warn('[Baustein] Auswahldialog nicht erreichbar:', e);
    }
    if (!angenommen) einmal(nummernEingabe(bausteine));
  });
}

/** Notnagel ohne React — etwa in einem Betrachter ohne Dialoge. */
function nummernEingabe(bausteine) {
  const liste = bausteine.map((b, i) => `${i + 1}. ${b.name}`).join('\n');
  const eingabe = globalThis.prompt(`Welcher Baustein?\n\n${liste}\n\nNummer eingeben:`, '1');
  if (!eingabe) return null;
  const idx = Number.parseInt(eingabe, 10) - 1;
  return Number.isInteger(idx) && idx >= 0 && idx < bausteine.length ? bausteine[idx] : null;
}

/** Eingeklappter Sub-Prozess mit Inhalt — und kein BRE-Behaelter. */
export function istHerausloesbar(element) {
  if (!element || !is(element, 'bpmn:SubProcess')) return false;
  if (isExpanded(element)) return false;
  const bo = element.businessObject;
  if ((bo?.flowElements || []).length === 0) return false;
  // Der Behaelter der Business-Rule-Exceptions gehoert nicht herausgeloest:
  // Die BRE-Navigation sucht ihre Eintraege in der Elementregistrierung, und
  // in einem Baustein stehen sie dort nicht mehr — die Sprünge aus dem
  // Prozess in die Beschreibung liefen stillschweigend ins Leere.
  return !istBreBehaelter(bo);
}

function istBreBehaelter(bo) {
  try {
    return bo?.get?.('custom:breHandler') === true;
  } catch {
    return bo?.$attrs?.['custom:breHandler'] === true || bo?.$attrs?.['custom:breHandler'] === 'true';
  }
}

/** Alles, was sich sinnvoll in einen Aufruf verwandeln lässt. */
function istEinfuegbar(element) {
  if (!element) return false;
  if (is(element, 'bpmn:SubProcess')) {
    if (isExpanded(element)) return false;
    return !istBreBehaelter(element.businessObject);
  }
  return is(element, 'bpmn:Task') || is(element, 'bpmn:CallActivity');
}

export { isBaustein };
